import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import useFilters from '../hooks/useFilters';
import { addLocale, locale } from 'primereact/api';

const FilterBar = ({
  onFilterChange,
  showAdvanced = false,
  enterprises = [],
  layouts = [],
  initialFilters = {},
  filters: parentFilters = undefined
}) => {
  const initial = parentFilters || initialFilters;
  const { filters, setFilters, updateFilter, clearFilters: resetHookFilters } = useFilters(initial);

  const normalizeFilters = (source = {}) => {
    if (!source) return {
      layoutName: null,
      enterpriseName: null,
      status: null,
      competenceStartDate: null,
      competenceEndDate: null,
    };

    const startMonthRaw = source.start_month || source.startMonth;
    const endMonthRaw = source.end_month || source.endMonth;

    const parseMonth = (raw) => {
      if (!raw || typeof raw !== 'string') return null;
      const [year, month] = raw.split('-');
      if (!year || !month) return null;
      const y = parseInt(year);
      const m = parseInt(month);
      if (Number.isNaN(y) || Number.isNaN(m)) return null;
      return new Date(y, m - 1, 1);
    };

    if (source.month && !startMonthRaw && !endMonthRaw) {
      const d = parseMonth(source.month);
      return {
        layoutName: source.layout_name || source.layoutName || null,
        enterpriseName: source.enterprise_name || source.enterpriseName || null,
        status: source.status || null,
        competenceStartDate: d,
        competenceEndDate: d,
      };
    }

    return {
      layoutName: source.layout_name || source.layoutName || null,
      enterpriseName: source.enterprise_name || source.enterpriseName || null,
      status: source.status || null,
      competenceStartDate: parseMonth(startMonthRaw),
      competenceEndDate: parseMonth(endMonthRaw),
    };
  };

  const [localFilters, setLocalFilters] = useState(normalizeFilters(initial));
  const [activeFilters, setActiveFilters] = useState([]);

  // Sincroniza filtros quando mudam externamente ou via hook
  useEffect(() => {
    const source = parentFilters || filters;
    setLocalFilters(normalizeFilters(source));
  }, [parentFilters, filters]);

  // Atualiza os chips de filtros ativos
  useEffect(() => {
    const active = [];
    if (localFilters.enterpriseName) active.push('Cliente');
    if (localFilters.layoutName) active.push('Layout');
    if (localFilters.status) active.push('Status');
    if (localFilters.competenceStartDate || localFilters.competenceEndDate) active.push('Competência');
    setActiveFilters(active);
  }, [localFilters]);

  const statusOptions = [
    { label: 'A Exportar', value: 'SUCCESS' },
    { label: 'Exportados', value: 'EXPORTED' },
    { label: 'Erro', value: 'FAILED' },
    { label: 'Informar Dados', value: 'MANUAL_PENDING' },
    { label: 'Pendentes', value: 'PENDING' },
    { label: 'Recebidos', value: 'RECEIVED' },
    { label: 'eContador', value: 'EC_EXPORTED' }
  ];

  const computeMonthValue = (date) => {
    if (date instanceof Date && !isNaN(date.getTime())) {
      const m = date.getMonth() + 1;
      const y = date.getFullYear();
      return `${y}-${String(m).padStart(2, '0')}`;
    }
    return '';
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);

    const filterToSend = {
      layout_name: newFilters.layoutName,
      enterprise_name: newFilters.enterpriseName,
      status: newFilters.status,
      start_month: computeMonthValue(newFilters.competenceStartDate),
      end_month: computeMonthValue(newFilters.competenceEndDate),
    };

    setFilters(filterToSend);
    // Evita enviar string vazia para o backend.
    // Se não estiver setado, deve virar `undefined` (ou null) para não filtrar indevidamente.
    const cleanedFilterToSend = {
      ...filterToSend,
      start_month: filterToSend.start_month ? filterToSend.start_month : undefined,
      end_month: filterToSend.end_month ? filterToSend.end_month : undefined,
    };

    setFilters(cleanedFilterToSend);
    if (onFilterChange) onFilterChange(cleanedFilterToSend);
  };


  const clearFilters = () => {
    const emptyFilters = {
      enterpriseName: null,
      layoutName: null,
      status: null,
      competenceStartDate: null,
      competenceEndDate: null,
    };

    setLocalFilters(emptyFilters);
    resetHookFilters();

    if (onFilterChange) {
      onFilterChange({
        enterprise_name: null,
        layout_name: null,
        status: null,
        start_month: null,
        end_month: null,
        month: null
      });
    }
  };

  const removeFilter = (filterName) => {
    switch (filterName) {
      case 'Layout':
        handleFilterChange('layoutName', null);
        break;
      case 'Cliente':
        handleFilterChange('enterpriseName', null);
        break;
      case 'Status':
        handleFilterChange('status', null);
        break;
      case 'Competência':
        setLocalFilters(prev => ({
          ...prev,
          competenceStartDate: null,
          competenceEndDate: null
        }));
        const filterToSend = {
          layout_name: localFilters.layoutName,
          enterprise_name: localFilters.enterpriseName,
          status: localFilters.status,
          start_month: null,
          end_month: null,
        };
        setFilters(filterToSend);
        if (onFilterChange) onFilterChange(filterToSend);
        break;
    }
  };

  return (
    <div style={{ marginLeft: '10px', marginRight: '10px' }}>
      {/* Active Filters Chips */}
      {activeFilters.length > 0 && (
        <div style={{ marginLeft: '-10px', alignItems: 'center' }} className="flex flex-wrap mb-2">
          <span className="text-sm text-gray-600 self-center mr-2">
            Filtros ativos:
          </span>
          {activeFilters.map((filter, index) => (
            <Chip
              key={index}
              label={filter}
              removable
              onRemove={() => removeFilter(filter)}
              className="text-sm"
              style={{ height: '25px' }}
            />
          ))}
          <Button
            label="Limpar todos"
            icon="pi pi-times-circle"
            className="p-button-text p-button-sm"
            onClick={clearFilters}
          />
        </div>
      )}

      {/* Main Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
        {/* Enterprise Filter */}
        <div className="flex flex-column gap-2">
          <label htmlFor="enterpriseFilter" className="text-sm font-medium text-gray-700">
            Cliente
          </label>
          <span className="p-input-icon-left w-full">
            <InputIcon className="pi pi-building" />
            <InputText
              id="enterpriseFilter"
              placeholder="Buscar cliente..."
              value={localFilters.enterpriseName || ''}
              onChange={(e) => handleFilterChange('enterpriseName', e.target.value)}
              className="w-full p-inputtext-sm"
            />
          </span>
        </div>

        {/* Layout Filter */}
        <div className="flex flex-column gap-2">
          <label htmlFor="layoutFilter" className="text-sm font-medium text-gray-700">
            Empresa - Layout
          </label>
          <span className="p-input-icon-left w-full">
            <InputIcon className="pi pi-list" />
            <InputText
              id="layoutFilter"
              placeholder="Buscar Empresa..."
              value={localFilters.layoutName || ''}
              onChange={(e) => handleFilterChange('layoutName', e.target.value)}
              className="w-full p-inputtext-sm"
            />
          </span>
        </div>

        {/* Competência Filter */}
        <div className="flex flex-column gap-2">
          <label className="text-sm font-medium text-gray-700">
            Competência
          </label>
          <div className="flex gap-2">
            <Calendar
              id="competenceStartMonthFilter"
              value={localFilters.competenceStartDate}
              onChange={(e) => handleFilterChange('competenceStartDate', e.value)}
              view="month"
              showButtonBar
              dateFormat="mm/yy"
              placeholder="Início"
              locale="pt-BR"
              readOnlyInput
              className="w-full p-inputtext-sm"
              // CRÍTICA: O início não pode ser maior que o fim selecionado
              maxDate={localFilters.competenceEndDate || undefined}
            />

            <Calendar
              id="competenceEndMonthFilter"
              value={localFilters.competenceEndDate}
              onChange={(e) => handleFilterChange('competenceEndDate', e.value)}
              view="month"
              showButtonBar
              dateFormat="mm/yy"
              placeholder="Fim"
              locale="pt-BR"
              readOnlyInput
              className="w-full p-inputtext-sm"            // CRÍTICA: O fim não pode ser menor que o início selecionado
              minDate={localFilters.competenceStartDate || undefined}
            />
          </div>
        </div>

        {/* Status Filter */}
        <div className="flex flex-column gap-2">
          <label htmlFor="statusFilter" className="text-sm font-medium text-gray-700">
            Status
          </label>
          <Dropdown
            id="statusFilter"
            value={localFilters.status}
            onChange={(e) => handleFilterChange('status', e.value)}
            options={statusOptions}
            placeholder="Selecione o status"
            className="w-full p-inputtext-sm"
          />
        </div>
      </div>
    </div>
  );
};

export default FilterBar;